"use client";

import { MessageSquare, Play, Settings2, UserPlus, type LucideIcon } from "lucide-react";
import { assignSigninLiffIfLineWebView, isAppSigninPath } from "@/lib/openLineExternalBrowser";
import { AeraButton } from "@/app/components/ui/AeraButton";

type Step = {
  step: string;
  title: string;
  description: string;
  Icon: LucideIcon;
};

const HOW_AERA_STEPS: Step[] = [
  {
    step: "1",
    title: "สัมภาษณ์", // Interview
    description:
      // Chat with AI workers to check their capabilities and see if they match your needs.
      "พูดคุยกับ AI เพื่อดูความสามารถ และเช็คว่าเหมาะกับงานของคุณหรือไม่",
    Icon: MessageSquare,
  },
  {
    step: "2",
    title: "จ้าง", // Hire
    description:
      // Select the best fit and add them to your team instantly with one click.
      "เลือกคนที่เหมาะที่สุด และเพิ่มเข้าทีมได้ทันทีในคลิกเดียว",
    Icon: UserPlus,
  },
  {
    step: "3",
    title: "ตั้งค่า", // Config
    description:
      // Customize the worker with your specific data, rules, and preferences.
      "ปรับแต่ง AI ด้วยข้อมูล กฎ และความต้องการเฉพาะของคุณ",
    Icon: Settings2,
  },
  {
    step: "4",
    title: "ทดสอบ", // Test
    description:
      // Verify performance in a safe environment before delegating real tasks.
      "ตรวจสอบผลลัพธ์ในสภาพแวดล้อมที่ปลอดภัย ก่อนมอบหมายงานจริง",
    Icon: Play,
  },
];

export function HowAeraWorksSection(props: {
  titleIcon?: React.ReactNode;
  showTitleIcon?: boolean;
  showCta?: boolean;
  ctaHref?: string;
  className?: string;
}) {
  const { titleIcon, showTitleIcon = true, showCta = false, ctaHref = "/signin", className } = props;

  return (
    <section
      className={
        className ??
        "scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
      }
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:justify-center md:gap-4">
          {showTitleIcon && titleIcon}
          <h2 className="text-xl font-bold tracking-tight text-aera-charcoal md:text-2xl">
            Aera ทำงานอย่างไร
          </h2>
        </div>
        <p className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-aera-gray">
          สร้างทีม AI ของคุณใน 4 ขั้นตอนที่เรียบง่าย เพื่อทำงานได้เร็วขึ้นและควบคุมได้ชัดเจน
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {HOW_AERA_STEPS.map(({ step, title, description, Icon }) => (
          <div
            key={title}
            className="flex flex-col items-center rounded-2xl bg-aera-white border border-aera-lightgray/80 p-4 text-center"
          >
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
              <Icon
                className="h-7 w-7 text-aera-amberdark"
                strokeWidth={1.75}
                aria-hidden
              />
            </div>
            <h3 className="text-base font-semibold text-aera-charcoal">
              {step}. {title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-aera-gray">
              {description}
            </p>
          </div>
        ))}
      </div>

      {showCta && (
        <AeraButton
          as="a"
          href={ctaHref}
          onClick={(e) => {
            if (isAppSigninPath(ctaHref) && assignSigninLiffIfLineWebView(ctaHref)) {
              e.preventDefault();
            }
          }}
          variant="primary"
          size="lg"
          paddingSize="xl"
          fontWeight="medium"
        >
          เริ่มใช้งาน
        </AeraButton>
      )}
    </section>
  );
}

